/**
 * 지역 모달 공용 스크립트 / Shared region-modal script
 *
 * 두 개의 독립 섹션이 이 파일 하나를 공유한다:
 * Two independent sections share this single file:
 *
 * <region-confirm-modal>  — 현재 쇼핑 지역을 확인해 주는 팝업 (sections/region-confirm-modal.liquid)
 *                           Popup confirming the current shopping region.
 * <region-redirect-modal> — 감지된 국가가 다르면 카운트다운 후 마켓 전환 (sections/region-redirect-modal.liquid)
 *                           Counts down and switches markets on a geo mismatch.
 *
 * 조율 규칙 / Coordination rules:
 * - 해제 기록은 STORAGE_KEY 하나를 공유 — 어느 쪽을 닫아도 둘 다 expiry 동안 숨김.
 *   One shared STORAGE_KEY — dismissing either hides both for the expiry.
 * - 같은 페이지에 둘 다 있으면 리다이렉트 모달이 우선: 확인 모달은 리다이렉트
 *   판정(REDIRECT_RESOLVED_EVENT)을 기다렸다가 리다이렉트가 없을 때만 열린다.
 *   When both are present the redirect modal wins: the confirm modal waits for
 *   the eligibility verdict (REDIRECT_RESOLVED_EVENT) and opens only when no
 *   redirect is due.
 * - 리다이렉트 직전에 해제 기록을 저장해 무한 리다이렉트 루프를 방지.
 *   The dismissal is persisted right before navigating to prevent loops.
 */
(() => {
  // 이미 정의된 경우 재정의 방지 (섹션 중복 렌더 대비)
  // Guard against double definition (duplicate section renders)
  if (customElements.get('region-confirm-modal')) return;

  const STORAGE_KEY = 'atacz:region-modal:dismissed';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const DETECT_TIMEOUT_MS = 3000;
  const REDIRECT_RESOLVED_EVENT = 'region-modal:redirect-resolved';

  // DOM 파싱 완료 후 실행 (다른 오버레이 섹션의 엘리먼트 존재 확인용)
  // Run after DOM parsing so sibling overlay sections are queryable
  const onDocumentReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  /**
   * 공통 베이스: 저장소, 열기/닫기, 포커스 트랩, ESC, 에디터 연동.
   * Shared base: storage, open/close, focus trap, Escape, editor integration.
   */
  class RegionModalBase extends HTMLElement {
    connectedCallback() {
      this.country = this.dataset.country || '';
      this.expiryDays = parseInt(this.dataset.expiryDays, 10) || 0;
      this.delayMs = (parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.testMode = this.dataset.testMode === 'true';

      // 해제 기록 저장소: 0일 = 세션 한정, 1일 이상 = 영구(localStorage)
      // Dismissal store: 0 days = session-scoped, 1+ days = persistent (localStorage)
      this.storage = this.expiryDays > 0 ? window.localStorage : window.sessionStorage;

      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick.bind(this));

      if (window.Shopify && window.Shopify.designMode) {
        this.bindEditorEvents();
        if (this.testMode) this.openFromEditor();
        return;
      }

      this.init();
    }

    /** 서브클래스별 초기화 / Subclass-specific initialisation */
    init() {}

    /** 에디터에서 열 때의 표시 방식 (서브클래스가 재정의 가능)
        How to present when opened from the editor (overridable) */
    openFromEditor() {
      this.open();
    }

    /**
     * 해제 기록이 아직 유효한지 확인 / Check whether a stored dismissal is still valid.
     */
    isDismissed() {
      try {
        const raw = this.storage.getItem(STORAGE_KEY);
        if (!raw) return false;

        const record = JSON.parse(raw);

        // 지역이 바뀌었으면 새 지역을 다시 확인시켜야 하므로 무효 처리
        // A different country means the new region must be re-confirmed
        if (record.country !== this.country) return false;

        // 세션 저장소는 존재 자체가 유효 / sessionStorage: presence alone is valid
        if (this.expiryDays === 0) return true;

        return Date.now() - record.at < this.expiryDays * DAY_MS;
      } catch (error) {
        // 저장소 접근 불가(사생활 보호 모드 등) 시 표시 쪽으로 폴백
        // Storage unavailable (private mode etc.) — fall back to showing
        return false;
      }
    }

    /**
     * 해제 기록 저장 / Persist the dismissal.
     */
    saveDismissal() {
      if (this.testMode) return;
      try {
        this.storage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), country: this.country }));
      } catch (error) {
        // 저장 실패는 치명적이지 않음 — 다음 페이지에서 다시 보일 뿐
        // Failure is non-fatal — the modal simply shows again on the next page
      }
    }

    open() {
      if (this.classList.contains('region-modal--open')) return;

      this.returnFocusTo = document.activeElement;
      this.removeAttribute('hidden');
      this.classList.add('region-modal--open');
      document.body.classList.add('region-modal-scroll-lock');
      document.addEventListener('keydown', this.onKeydown);

      // 접근성: 열리면 닫기 버튼(숨김 설정 시 기본 버튼)으로 포커스 이동
      // Accessibility: move focus to the close button (or the primary button
      // when the close button is hidden via settings) when opened
      const focusTarget =
        this.querySelector('.region-modal__close') || this.querySelector('.region-modal__button-primary');
      if (focusTarget) focusTarget.focus();
    }

    close(persist = true) {
      if (!this.classList.contains('region-modal--open')) return;

      this.classList.remove('region-modal--open');
      this.setAttribute('hidden', '');
      document.body.classList.remove('region-modal-scroll-lock');
      document.removeEventListener('keydown', this.onKeydown);

      if (persist) this.saveDismissal();

      // 접근성: 모달을 연 시점의 포커스 위치 복원
      // Accessibility: restore focus to where it was before opening
      if (this.returnFocusTo && typeof this.returnFocusTo.focus === 'function') {
        this.returnFocusTo.focus();
      }
    }

    onClick(event) {
      // 닫기 트리거: 오버레이 / X 버튼 / 유지 버튼
      // Close triggers: backdrop / X button / stay buttons
      if (event.target.closest('[data-region-modal-close]')) {
        this.close();
      }
    }

    onKeydown(event) {
      // ESC로 닫기 / Close on Escape
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      // 단순 포커스 트랩: Tab 순환을 모달 내부로 제한
      // Simple focus trap: keep Tab cycling inside the modal
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.querySelectorAll('button, a[href], select, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /**
     * 테마 에디터 연동: 섹션 선택 시 열고, 해제 시 닫는다.
     * Theme editor integration: open on section select, close on deselect.
     */
    bindEditorEvents() {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.openFromEditor();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
      });
    }
  }

  /**
   * 지역 확인 모달 — 현재 마켓이 방문자에게 맞음을 확인시켜 주는 팝업.
   * 리다이렉트 모달이 같은 페이지에 있으면 그 판정을 기다린다.
   * Region confirm modal — confirms the current market fits the visitor.
   * Defers to the redirect modal's verdict when one is present.
   */
  class RegionConfirmModal extends RegionModalBase {
    init() {
      if (this.testMode) {
        this.open();
        return;
      }
      if (this.isDismissed()) return;

      onDocumentReady(() => {
        const redirectModal = document.querySelector('region-redirect-modal');
        if (!redirectModal) {
          this.scheduleOpen();
          return;
        }

        // 리다이렉트 판정 대기 — 리다이렉트가 없을 때만 열기
        // Wait for the redirect verdict — open only when no redirect is due
        if (redirectModal.redirectResolved) {
          if (!redirectModal.willRedirect) this.scheduleOpen();
        } else {
          document.addEventListener(
            REDIRECT_RESOLVED_EVENT,
            (event) => {
              if (!event.detail.willRedirect) this.scheduleOpen();
            },
            { once: true }
          );
        }
      });
    }

    scheduleOpen() {
      setTimeout(() => this.open(), this.delayMs);
    }

    onClick(event) {
      super.onClick(event);

      // "Change Country or Currency" → 내장 국가 선택 폼 토글
      // "Change Country or Currency" → toggle the built-in country selector
      const switchButton = event.target.closest('[data-region-modal-switch]');
      if (switchButton) {
        const selector = this.querySelector('.region-modal__selector');
        if (!selector) return;

        const expanded = switchButton.getAttribute('aria-expanded') === 'true';
        switchButton.setAttribute('aria-expanded', String(!expanded));
        selector.toggleAttribute('hidden', expanded);

        if (!expanded) {
          const select = selector.querySelector('select');
          if (select) select.focus();
        }
      }
    }
  }

  /**
   * 자동 리다이렉트 모달 — 감지 국가가 현재 마켓과 다르면 카운트다운 후
   * 네이티브 localization 폼을 제출해 마켓을 전환한다.
   * Auto-redirect modal — on a geo mismatch, counts down and submits the
   * native localization form to switch markets.
   */
  class RegionRedirectModal extends RegionModalBase {
    init() {
      this.countdownSeconds = parseInt(this.dataset.countdown, 10) || 5;
      this.targetCountries = (this.dataset.targetCountries || '').split(',').filter(Boolean);
      this.countdownTimer = null;
      this.redirectTarget = null;
      this.redirectResolved = false;
      this.willRedirect = false;

      if (this.testMode) {
        // 테스트 모드: 실제 리다이렉트 없이 미리보기만, 확인 모달은 양보시키지 않음
        // Testing: preview only, never redirects; does not suppress the confirm modal
        this.resolve(false);
        this.openPreview();
        return;
      }

      if (this.isDismissed()) {
        this.resolve(false);
        return;
      }

      this.prepare();
    }

    disconnectedCallback() {
      this.stopCountdown();
    }

    /** 판정 결과를 확인 모달에 알린다 / Publish the verdict for the confirm modal */
    resolve(willRedirect) {
      this.redirectResolved = true;
      this.willRedirect = willRedirect;
      document.dispatchEvent(new CustomEvent(REDIRECT_RESOLVED_EVENT, { detail: { willRedirect } }));
    }

    /**
     * 리다이렉트 판정: 감지 국가가 현재 마켓과 다르고, Country 블록에 설정돼
     * 있으며, 전환 폼에 옵션이 있을 때만 발동. 그 외에는 아무것도 하지 않는다.
     * Eligibility gate: fires only when the detected country differs from the
     * current market, is configured as a Country block, and exists in the
     * localization form. Does nothing otherwise.
     */
    async prepare() {
      const target = await this.detectCountry();
      const eligible =
        target &&
        target.handle !== this.country &&
        this.targetCountries.includes(target.handle) &&
        this.querySelector(`.region-modal__country-select option[value="${target.handle}"]`);

      this.resolve(Boolean(eligible));
      if (!eligible) return;

      setTimeout(() => this.openRedirect(target), this.delayMs);
    }

    /**
     * Shopify 위치 감지 — 실패/타임아웃 시 null 반환.
     * Shopify geolocation suggestion — returns null on failure/timeout.
     */
    async detectCountry() {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), DETECT_TIMEOUT_MS);
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(`${root}browsing_context_suggestions.json`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!response.ok) return null;

        const data = await response.json();
        const detected = data && data.detected_values && data.detected_values.country;
        if (!detected || !detected.handle) return null;

        return { handle: String(detected.handle).toUpperCase(), name: detected.name || detected.handle };
      } catch (error) {
        return null;
      }
    }

    /** [country] 토큰 채우기 / Fill the [country] tokens */
    fillCountryTokens(name) {
      this.querySelectorAll('[data-redirect-country]').forEach((el) => {
        el.textContent = name;
      });
    }

    openRedirect(target) {
      this.redirectTarget = target;
      this.fillCountryTokens(target.name);
      this.open();
      this.startCountdown();
    }

    /**
     * 테스트/에디터 미리보기: 첫 번째 타깃 국가(현재 국가 제외)의 이름으로
     * 토큰을 채워서 보여 주되, 카운트다운·리다이렉트는 하지 않는다.
     * Testing/editor preview: fills tokens with the first non-current target
     * country's name; no countdown, no redirect.
     */
    openPreview() {
      const previewHandle = this.targetCountries.find((code) => code !== this.country) || this.country;
      const option = this.querySelector(`.region-modal__country-select option[value="${previewHandle}"]`);
      this.fillCountryTokens((option && option.dataset.countryName) || previewHandle);
      this.querySelectorAll('[data-redirect-countdown]').forEach((el) => {
        el.textContent = String(this.countdownSeconds);
      });
      this.open();
    }

    openFromEditor() {
      this.openPreview();
    }

    startCountdown() {
      let remaining = this.countdownSeconds;
      const render = () => {
        this.querySelectorAll('[data-redirect-countdown]').forEach((el) => {
          el.textContent = String(remaining);
        });
      };
      render();

      this.countdownTimer = setInterval(() => {
        remaining -= 1;
        render();
        if (remaining <= 0) {
          this.stopCountdown();
          this.performRedirect();
        }
      }, 1000);
    }

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }

    /**
     * 네이티브 localization 폼을 제출해 마켓을 전환한다.
     * Submit the native localization form to switch markets.
     */
    performRedirect() {
      const form = this.querySelector('.region-modal__localization-form');
      const select = form && form.querySelector('select[name="country_code"]');
      if (!form || !select || !this.redirectTarget) {
        this.close(false);
        return;
      }

      select.value = this.redirectTarget.handle;

      // 리다이렉트 루프 방지: 이동 전에 해제 기록을 남겨, 사용자가 의도적으로
      // 돌아와도 만료 전까지는 다시 리다이렉트하지 않는다.
      // Loop guard: persist the dismissal before navigating so a visitor who
      // deliberately comes back is not redirected again until it expires.
      this.saveDismissal();
      form.submit();
    }

    close(persist = true) {
      // 닫기 = 카운트다운 취소(현재 스토어 유지 선택으로 간주)
      // Closing cancels the countdown (treated as choosing to stay)
      this.stopCountdown();
      super.close(persist);
    }

    onClick(event) {
      super.onClick(event);

      // "Take me there" → 카운트다운 없이 즉시 전환 (테스트 모드는 미리보기 유지)
      // "Take me there" → switch immediately (previews in testing stay put)
      if (event.target.closest('[data-region-modal-switch-now]')) {
        this.stopCountdown();
        if (this.testMode || (window.Shopify && window.Shopify.designMode)) return;
        this.performRedirect();
      }
    }
  }

  customElements.define('region-confirm-modal', RegionConfirmModal);
  customElements.define('region-redirect-modal', RegionRedirectModal);
})();
